import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import orderBy from 'lodash/orderBy';

import {
  IAbsenceStatusDTO,
  IAbsenceRequestDTO,
  IApproverDTO,
  IScheduledAbsenceDTO,
  IAbsenceType,
  IAbsenceRequestResultDTO,
} from 'src/api/dtos';
import { IError } from 'src/api/types';

import {
  createAbsence,
  fetchAbsences,
  fetchAbsenceStatuses,
  fetchAbsenceTypes,
  fetchApprovers,
  fetchScheduledAbsences,
  cancelAbsence,
} from './absences.actions';
import { IAbsenceState } from './absences.types';

const initialState: IAbsenceState = {
  data: [],
  item: null,
  scheduled: [],
  approvers: [],
  statuses: [],
  types: [],
  loadingData: false,
  loadingScheduled: false,
  submitting: false,
  error: null,
  overlappingHoliday: null,
};

export const absencesSlice = createSlice({
  name: 'absences',
  initialState,
  reducers: {
    resetItem: (state: Draft<IAbsenceState>) => {
      state.item = null;
    },
  },
  extraReducers: (build) => {
    /* fetchAbsences */
    build.addCase(
      fetchAbsences.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IAbsenceRequestDTO[]>) => {
        return {
          ...state,
          data: payload,
          loadingData: false,
        };
      },
    );
    build.addCase(fetchAbsences.pending.type, (state: Draft<IAbsenceState>) => {
      return {
        ...state,
        error: null,
        loadingData: true,
      };
    });
    build.addCase(fetchAbsences.rejected.type, (state: Draft<IAbsenceState>, { payload }: PayloadAction<IError>) => {
      return {
        ...state,
        error: payload,
        loadingData: false,
      };
    });
    /* createAbsence */
    build.addCase(
      createAbsence.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IAbsenceRequestResultDTO>) => {
        const { absenceRequestInformation, overlappingHoliday } = payload;

        if (!absenceRequestInformation) {
          return {
            ...state,
            submitting: false,
            overlappingHoliday,
          };
        }

        const item: IAbsenceRequestDTO = {
          ...absenceRequestInformation,
          absenceTypeName:
            state.types.find((type) => type.id === absenceRequestInformation.absenceTypeId)?.description ||
            absenceRequestInformation.absenceTypeName,
        };

        return {
          ...state,
          data: orderBy([item, ...state.data], ['startDate'], ['desc']),
          item,
          submitting: false,
        };
      },
    );
    build.addCase(createAbsence.pending.type, (state: Draft<IAbsenceState>) => {
      return {
        ...state,
        submitting: true,
        item: null,
      };
    });
    build.addCase(createAbsence.rejected.type, (state: Draft<IAbsenceState>) => {
      return {
        ...state,
        submitting: false,
        item: null,
      };
    });
    /* cancelAbsence */
    build.addCase(
      cancelAbsence.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IAbsenceRequestDTO[]>) => {
        const newData = state.data.map((item) => {
          const newItem = payload.find((payloadItem) => payloadItem.notificationId === item.notificationId);
          return newItem ? { ...item, ...newItem } : { ...item };
        });

        const [item] = payload;

        return {
          ...state,
          data: newData,
          item,
          submitting: false,
        };
      },
    );
    build.addCase(cancelAbsence.pending.type, (state: Draft<IAbsenceState>) => {
      return {
        ...state,
        submitting: true,
      };
    });
    build.addCase(cancelAbsence.rejected.type, (state: Draft<IAbsenceState>) => {
      return {
        ...state,
        submitting: false,
      };
    });
    /* fetchScheduledAbsences */
    build.addCase(
      fetchScheduledAbsences.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IScheduledAbsenceDTO[]>) => {
        return {
          ...state,
          scheduled: payload,
          loadingScheduled: false,
        };
      },
    );
    build.addCase(fetchScheduledAbsences.pending.type, (state: Draft<IAbsenceState>) => {
      return {
        ...state,
        error: null,
        loadingScheduled: true,
      };
    });
    build.addCase(
      fetchScheduledAbsences.rejected.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IError>) => {
        return {
          ...state,
          error: payload,
          loadingScheduled: false,
        };
      },
    );
    /* fetchApprovers */
    build.addCase(
      fetchApprovers.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IApproverDTO[]>) => {
        return {
          ...state,
          approvers: payload,
        };
      },
    );
    /* fetchAbsenceStatuses */
    build.addCase(
      fetchAbsenceStatuses.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IAbsenceStatusDTO[]>) => {
        return {
          ...state,
          statuses: payload,
        };
      },
    );
    /* fetchAbsenceTypes */
    build.addCase(
      fetchAbsenceTypes.fulfilled.type,
      (state: Draft<IAbsenceState>, { payload }: PayloadAction<IAbsenceType[]>) => {
        return {
          ...state,
          types: payload,
        };
      },
    );
  },
});

const { actions } = absencesSlice;

export const { resetItem: resetNewAbsenceActionCreator } = actions;
